import { botResponse$ } from "./chatStream";
import store from "../../../../app/store";
import moment from "moment";
import {
  addStreamingBotMessage,
  appendStreamingChunk,
  completeStreamingBotMessage,
  setExistingThreadID,
  setExistingThreadName,
  setStreaming,
  setThreadDetails,
} from "../../../../rtk-query/features/chatbot/chatbotSlice";
import { fetchThreadList } from "../../../../rtk-query/features/chatbot/chatbotSidebarToolsSlice";

let isSubscribed = false;
let subscription = null;

let currentBotMessageId = null;
let metadata = null;

// ✅ ONLY ONE SOURCE OF TRUTH
// Typing queue
let typingQueue = "";
let typingTimer = null;
let responseFinished = false;

function startTyping() {
  if (typingTimer || !currentBotMessageId) return;

  typingTimer = setInterval(() => {
    if (!typingQueue.length) {
      clearInterval(typingTimer);
      typingTimer = null;

      // If server already said response is finished,
      // stop the loader after remaining text is typed.
      if (!typingQueue.length) {
        clearInterval(typingTimer);
        typingTimer = null;
        return;
      }
      return;
    }

    // Type 2 characters every tick
    let size = 2;

    if (typingQueue.length > 1500) {
      size = 30;
    } else if (typingQueue.length > 700) {
      size = 20;
    } else if (typingQueue.length > 300) {
      size = 12;
    } else if (typingQueue.length > 100) {
      size = 6;
    }

    let end = size;

    while (
      end < typingQueue.length &&
      !/[\s.,!?;:\n`*|)/]/.test(typingQueue[end])
    ) {
      end++;
    }

    const chunk = typingQueue.slice(0, end);
    typingQueue = typingQueue.slice(end);

    store.dispatch(
      appendStreamingChunk({
        id: currentBotMessageId,
        chunk,
      }),
    );
  }, 25);
}

function scheduleChunk(chunk) {
  typingQueue += chunk;
  startTyping();
}

//Old code
// function parseChunk(rawData) {
//   const lines = rawData.split("\n");

//   let result = "";

//   for (const line of lines) {
//     const trimmed = line.trimStart();

//     // Handle SSE data lines
//     if (line.startsWith("data:")) {
//       const content = line.replace(/^data:\s?/, "");

//       // Ignore empty SSE events (e.g. "data:")
//       if (!content.trim()) {
//         continue;
//       }

//       result += content;
//       continue;
//     }

//     // Horizontal rule
//     if (trimmed === "---") {
//       result += "\n\n---\n\n";
//     }
//     // Code block
//     else if (trimmed === "```") {
//       result += "\n```\n";
//     }
//     // Numbered list after bold heading
//     else if (result.endsWith("**") && /^[0-9]/.test(trimmed)) {
//       result += "\n\n" + line;
//     }
//     // Markdown headings / bold
//     else if (trimmed.startsWith("**") || trimmed.startsWith("#")) {
//       result += "\n\n" + trimmed;
//     }
//     // New paragraph for capitalized sentence
//     else if (/^[A-Z]/.test(trimmed)) {
//       result += "\n\n" + trimmed;
//     }
//     // Lists / tables / code
//     else if (
//       line.startsWith("- ") ||
//       line.startsWith("* ") ||
//       line.startsWith("|") ||
//       line.startsWith("```") ||
//       line.startsWith("✓") ||
//       /^\d+\.\s/.test(trimmed) ||
//       /^\d+$/.test(trimmed)
//     ) {
//       result += "\n" + line;
//     }
//     // Everything else
//     else {
//       result += line;
//     }
//   }

//   return result;
// }

function appendSSEContent(result, content) {
  // Preserve blank SSE events as separators
  if (!content.trim()) {
    if (result && !/\s$/.test(result)) {
      return result + " ";
    }
    return result;
  }

  // Prevent words/numbers from sticking together
  if (
    result &&
    ((/[A-Za-z)]$/.test(result) && /^[0-9]/.test(content)) ||
      (/[0-9]$/.test(result) && /^[A-Za-z]/.test(content)))
  ) {
    result += " ";
  }

  return result + content;
}

function isListLine(line, trimmed) {
  return (
    line.startsWith("- ") ||
    line.startsWith("* ") ||
    line.startsWith("|") ||
    line.startsWith("```") ||
    line.startsWith("✓") ||
    /^\d+\.\s/.test(trimmed) ||
    /^\d+$/.test(trimmed)
  );
}

function formatMarkdown(result, line, trimmed) {
  if (trimmed === "---") {
    return result + "\n\n---\n\n";
  }

  if (trimmed === "```") {
    return result + "\n```\n";
  }

  if (result.endsWith("**") && /^[0-9]/.test(trimmed)) {
    return result + "\n\n" + line;
  }

  if (trimmed.startsWith("**") || trimmed.startsWith("#")) {
    return result + "\n\n" + trimmed;
  }

  if (/^[A-Z]/.test(trimmed)) {
    return result + "\n\n" + trimmed;
  }

  if (isListLine(line, trimmed)) {
    return result + "\n" + line;
  }

  return result + line;
}

function parseChunk(rawData) {
  const lines = rawData.split("\n");
  let result = "";

  for (const line of lines) {
    const trimmed = line.trimStart();

    if (line.startsWith("data:")) {
      const content = line.replace(/^data:\s?/, "");
      result = appendSSEContent(result, content);
      continue;
    }

    result = formatMarkdown(result, line, trimmed);
  }

  return result;
}

export const initChatStreamListener = () => {
  if (isSubscribed) return;

  isSubscribed = true;

  subscription = botResponse$.subscribe((event) => {
    const rawData = event?.data;
    if (!rawData) return;

    let value = parseChunk(rawData);

    const trimmedValue = value.trim();
    if (!trimmedValue) return;

    if (trimmedValue.includes("[RESPONSE_DONE]")) {
      value = value.replace("[RESPONSE_DONE]", "");

      if (value.trim()) {
        if (!currentBotMessageId) {
          currentBotMessageId = `${Date.now()}-${crypto.getRandomValues(new Uint32Array(1))[0]}`;

          store.dispatch(
            addStreamingBotMessage({
              id: currentBotMessageId,
              dateTime: moment(),
            }),
          );
        }

        scheduleChunk(value);
      }

      // Wait until the queue finishes typing
      responseFinished = true;

      return;
    }

    if (trimmedValue.includes("[DONE]")) {
      try {
        // Wait until everything is typed
        const finish = () => {
          store.dispatch(
            completeStreamingBotMessage({
              id: currentBotMessageId,
              feedbackId: metadata?.Id,
              isLike: metadata?.isLike,
            }),
          );

          if (metadata) {
            store.dispatch(
              setThreadDetails({
                threadId: metadata.ThreadId,
                threadName: metadata.ThreadName,
              }),
            );

            store.dispatch(fetchThreadList());

            store.dispatch(setExistingThreadID(metadata.ThreadId));
            store.dispatch(setExistingThreadName(metadata.ThreadName));
          }

          store.dispatch(setStreaming(false));

          currentBotMessageId = null;
          metadata = null;
          typingQueue = "";
          responseFinished = false;
        };

        if (typingQueue.length || typingTimer) {
          const wait = setInterval(() => {
            if (!typingQueue.length && !typingTimer) {
              clearInterval(wait);
              finish();
            }
          }, 30);
        } else {
          finish();
        }

        return;
      } catch (e) {
        console.error("Error completing stream:", e);
      }

      return;
    }

    const jsonMatch = trimmedValue.match(/\{[\s\S]*\}/);

    if (jsonMatch) {
      try {
        metadata = JSON.parse(jsonMatch[0]);
      } catch (e) {
        console.error("Metadata parse error", jsonMatch[0], e);
        store.dispatch(setStreaming(false));
      }
      return;
    }

    if (!currentBotMessageId) {
      currentBotMessageId = `${Date.now()}-${crypto.getRandomValues(new Uint32Array(1))[0]}`;

      store.dispatch(
        addStreamingBotMessage({
          id: currentBotMessageId,
          dateTime: moment(),
        }),
      );

      scheduleChunk(value);
      return;
    }

    scheduleChunk(value);
  });
};

export const destroyChatStreamListener = () => {
  if (typingTimer) {
    clearInterval(typingTimer);
    typingTimer = null;
  }

  typingQueue = "";
  responseFinished = false;

  if (subscription) {
    subscription.unsubscribe();
    subscription = null;
  }

  isSubscribed = false;
};
